// Presentation only: the runtime's existing incoming-call set owns the lifetime.
export class IncomingFavicon {
  private active = false;
  private visible = true;
  private generation = 0;
  private icon?: { element: HTMLLinkElement; created: boolean; badge: string; plain: string; attributes: (string | null)[] };

  setVisible(visible: boolean): void {
    this.visible = visible;
    const icon = this.icon;
    if (!icon || !icon.element.isConnected) return;
    const href = icon.element.getAttribute("href");
    if (href === icon.badge || href === icon.plain) icon.element.href = visible ? icon.badge : icon.plain;
  }

  async setActive(active: boolean): Promise<void> {
    if (this.active === active) return;
    this.active = active;
    const generation = ++this.generation;
    if (!active) {
      const icon = this.icon;
      this.icon = undefined;
      // Do not overwrite a newer icon installed by the host application.
      if (icon && [icon.badge, icon.plain].includes(icon.element.getAttribute("href") || "")) {
        if (icon.created) icon.element.remove();
        else ["href", "type", "sizes"].forEach((name, index) => {
          const value = icon.attributes[index];
          if (value === null) icon.element.removeAttribute(name);
          else icon.element.setAttribute(name, value);
        });
      }
      return;
    }

    const original = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    const source = original?.getAttribute("href");
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 32;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Incoming favicon canvas is unavailable");
    if (source) {
      const image = await loadIcon(original.href);
      context.drawImage(image, 0, 0, 32, 32);
    }
    const plain = canvas.toDataURL("image/png");
    context.beginPath();
    context.arc(25, 7, 6, 0, Math.PI * 2);
    context.fillStyle = "#e53935";
    context.fill();
    context.lineWidth = 2;
    context.strokeStyle = "#fff";
    context.stroke();

    if (!this.active || generation !== this.generation) return;
    if (original && (!original.isConnected || original.getAttribute("href") !== source)) return;
    const element = original || document.createElement("link");
    const badge = canvas.toDataURL("image/png");
    this.icon = { element, badge, plain, created: !original,
      attributes: ["href", "type", "sizes"].map(name => element.getAttribute(name)) };
    if (!original) element.rel = "icon";
    element.type = "image/png";
    element.sizes.value = "32x32";
    element.href = this.visible ? badge : plain;
    if (!original) document.head.append(element);
  }
}

function loadIcon(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const finish = (error?: Error) => {
      clearTimeout(timer);
      image.onload = image.onerror = null;
      if (error) reject(error);
      else resolve(image);
    };
    const timer = setTimeout(() => finish(new Error("Incoming favicon load timed out")), 5000);
    image.onload = () => finish();
    image.onerror = () => finish(new Error("Incoming favicon could not be loaded"));
    if (new URL(url, document.baseURI).origin !== location.origin) image.crossOrigin = "anonymous";
    image.src = url;
  });
}
